import { z } from 'zod';
import { counter } from './counts.js';
import { pageOf } from './paging.js';
/**
 * Segments, over the wire.
 *
 * The rule shapes are duplicated from the domain rather than imported, because
 * `contracts` may not depend on `domain` — and the duplication is the point:
 * this is what a *client* is allowed to send, checked before anything is built
 * from it. The domain then validates the meaning (is "at least" sensible for a
 * company name), which is a different question from the shape.
 */
const segmentField = z.enum([
    'status',
    'suppression',
    'name',
    'email',
    'company',
    'lastSentDays',
    'custom',
]);
const segmentOperator = z.enum([
    'is',
    'is_not',
    'contains',
    'not_contains',
    'is_empty',
    'is_not_empty',
    'at_least',
    'at_most',
]);
const segmentRuleSchema = z.object({
    field: segmentField,
    /**
     * Which declared field, for `custom` only.
     *
     * The length cap is the shape; the domain decides whether it is a key it
     * can bind into a jsonb lookup. It reaches the query as a parameter either
     * way — see `segmentToSql`.
     */
    key: z.string().max(40).optional(),
    operator: segmentOperator,
    /** Always a string; the field and operator decide how it is read. */
    value: z.string().max(200),
});
/**
 * One level of nesting, and no more.
 *
 * "A and (B or C)" is what people build. Arbitrary depth turns a readable
 * builder into a formula editor, and turns the SQL translation into something
 * much harder to keep parameterised — which is the part that matters, because
 * that translation decides who receives mail.
 */
const segmentGroupSchema = z.object({
    any: z.array(segmentRuleSchema).max(20),
});
export const segmentDefinitionSchema = z.object({
    match: z.enum(['all', 'any']),
    children: z.array(z.union([segmentRuleSchema, segmentGroupSchema])).max(20),
});
const segmentName = z.string().trim().min(1, 'a segment needs a name').max(120);
export const saveSegmentRequestSchema = z.object({
    name: segmentName,
    definition: segmentDefinitionSchema,
    /**
     * Values for the columns this organisation declared on its segments.
     *
     * **Absent means leave them as they are**, which is not how a contact's work
     * and is deliberate: two different screens write a segment. The rule builder
     * edits the rules and knows nothing about declared columns; the board edits
     * the columns and does not touch the rules. Requiring the whole set would
     * mean the builder wiped every value it had never been told about.
     *
     * Keys the server does not recognise are dropped rather than stored, so a
     * stale bundle cannot write a column that no longer exists.
     */
    custom: z.record(z.string().max(40), z.string().max(500)).optional(),
});
export const segmentSchema = z.object({
    segmentId: z.string(),
    name: z.string(),
    definition: segmentDefinitionSchema,
    /** Values for the declared columns, keyed by field key. */
    custom: z.record(z.string(), z.string()),
    /** Contacts connected to it. Not what its rules reach — see the repository. */
    connected: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const segmentListSchema = z.object({ segments: z.array(segmentSchema) });
/**
 * Saving answers one of three ways, and the caller can act on each.
 *
 * `invalid` carries the reason per rule, because a segment builder has to point
 * at the rule that is wrong rather than refusing the whole form. `name-taken`
 * is separate because it is about the name, not the rules.
 */
export const saveSegmentResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('saved'), segmentId: z.string() }),
    z.object({ status: z.literal('name-taken') }),
    z.object({ status: z.literal('invalid'), problems: z.array(z.string()) }),
]);
/**
 * How many contacts a definition reaches right now.
 *
 * Takes a definition rather than an id, because the only moment the number is
 * useful is while someone is still building the segment.
 */
export const segmentCountResponseSchema = z.object({
    reach: counter,
});
/**
 * Who a segment reaches right now.
 *
 * Thinner than a contact row on purpose: the question is whether the right
 * people are in the segment. A suppression state per row would invite reading
 * this as the send list, and it is not — suppression is re-read when each
 * message is claimed, so this list and the audience that receives mail differ
 * by design. (I5)
 */
/** `pageOf`, like every other paged listing, so one component renders them all. */
export const segmentMembersSchema = pageOf(z.object({ contactId: z.string(), email: z.string(), name: z.string() }));
/**
 * Connecting contacts to a segment, or taking them off it.
 *
 * Exactly one of `contactIds` and `allMatching`. A tick list is the first;
 * "everybody this filter finds" is the second, and it is sent as the filter
 * rather than as the ids it currently resolves to — a browser that expanded
 * forty thousand rows would be sending a list that was already out of date, and
 * the server would connect people somebody never saw.
 *
 * `allMatching` is the board's own question, spelt the same way the listing
 * sends it, so what gets connected is what was on screen. It carries **both**
 * halves: the view whose tab is open and the extra rules typed into the filter.
 * It used to carry only the view, so narrowing the board and then connecting
 * "everybody matching" connected everybody in the tab — the rules somebody
 * could see were silently ignored, and the audience was wrong.
 */
export const changeMembershipRequestSchema = z
    .object({
    contactIds: z.array(z.string()).max(1000).optional(),
    allMatching: z
        .object({
        search: z.string(),
        /** The open view's id; its rules are resolved by the server. */
        view: z.string().optional(),
        /** The board's own filter, on top of the view rather than instead of it. */
        filter: segmentDefinitionSchema.optional(),
    })
        .optional(),
})
    .refine((body) => (body.contactIds === undefined) !== (body.allMatching === undefined), 'say either which contacts or which filter, and not both');
/**
 * How many rows actually changed.
 *
 * Not how many were asked for: connecting sixty people when twenty are already
 * connected changes forty, and the screen says forty. The difference is the
 * honest answer to "did that work", and a number that always equalled the
 * selection would answer nothing.
 */
export const membershipChangedSchema = z.object({ changed: z.number().int().min(0) });
//# sourceMappingURL=segments.js.map